import React from "react";
import { User } from "../../../../../../../models/Entities";
import MergeTable from "../../../../../../shared/MergeTable";
import { PRODUCT_ORDER, SkeletonLoader, Tooltip } from "@merge-api/merge-javascript-shared";
import { Info } from "lucide-react";
import MembersTableRow from "./MembersTableRow";
import EmptyStateWrapper from "../../../../../../shared/EmptyStateWrapper";
import useAppContext from "../../../../../../context/useAppContext";

type MembersTableProps = {
  users?: User[];
  isLoading: boolean;
  onUserChange: (matches: (user: User) => boolean, replace: (user: User) => User) => void;
};

const MembersTable = ({ users = [], isLoading, onUserChange }: MembersTableProps) => {
  const { user: meUser } = useAppContext();
  const productSwitcher = meUser.product_switcher;
  const productOptions =
    meUser.account_bridge_enabled && productSwitcher
      ? PRODUCT_ORDER.filter((product) =>
          productSwitcher.products.some((entry) => entry.product === product),
        )
      : [];
  const columnCount = productOptions.length ? 5 : 4;

  return (
    <>
      <MergeTable
        noBoxShadow
        hasMorePaddingOnFirstElement
        hover={false}
        header={
          <>
            <th scope="col">Name & email address</th>
            <th scope="col">Role</th>
            <th scope="col">Two-factor authentication</th>
            {productOptions.length > 0 && (
              <th scope="col" className="w-[26rem]">
                Products{" "}
                <Tooltip title="Choose which products this member can access">
                  <Info size={12} />
                </Tooltip>
              </th>
            )}
          </>
        }
        content={
          isLoading ? (
            Array.from({ length: 5 }).map((_, index) => (
              <tr key={`skeleton-row-${index}`}>
                <td colSpan={columnCount}>
                  {/* 48 is a loaded row's name line plus email line, so the bar matches its height */}
                  <SkeletonLoader height={48} borderRadius={8} fullWidth />
                </td>
              </tr>
            ))
          ) : users.length ? (
            users.map((user) => (
              <MembersTableRow
                key={user.id}
                user={user}
                productOptions={productOptions}
                onProductsSaved={(savedUser) =>
                  onUserChange(
                    (current) => current.id === user.id,
                    () => savedUser,
                  )
                }
              />
            ))
          ) : (
            <tr>
              <td colSpan={columnCount} className="py-6">
                <EmptyStateWrapper isTable />
              </td>
            </tr>
          )
        }
      />
    </>
  );
};

export default MembersTable;
